/*
 * File: CurrencyInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/6/2023
 * Copyright (c) DrivingDoc 2023
 */
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

interface CurrencyInputProps {
  name: string;
  label: string;
  textFieldProps?: TextFieldProps;
  /** When true, empty field saves as null instead of 0 */
  allowNull?: boolean;
  /** Placeholder text shown when field is empty (only applies when allowNull is true) */
  placeholder?: string;
}

const defaultTextFieldProps: TextFieldProps = {
  variant: 'standard',
  InputLabelProps: {
    shrink: true,
  },
  InputProps: {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  },
};

export default function CurrencyInput({
  name,
  label,
  textFieldProps = defaultTextFieldProps,
  allowNull = false,
  placeholder,
}: CurrencyInputProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  // Determine the display value
  // const displayValue = (() => {
  //   if (allowNull && (field.value === null || field.value === undefined)) {
  //     return '';
  //   }
  //   return typeof field.value === 'number' ? field.value / 100 : 0;
  // })();

  return (
    <NumericFormat
      customInput={TextField}
      name={name}
      label={label}
      variant="standard"
      fullWidth
      placeholder={allowNull ? placeholder : undefined}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        },
        inputLabel: {
          shrink: true,
        },
      }}
      error={!!fieldState.error}
      helperText={fieldState.error?.message}
      getInputRef={field.ref}
      // value={displayValue}
      // @ts-expect-error: field.value is unknown, but it is safely at runtime
      value={typeof field.value === 'number' ? field.value / 100 : 0}
      onBlur={field.onBlur}
      onValueChange={(values) => {
        if (allowNull && (values?.floatValue === undefined || values?.floatValue === null)) {
          field.onChange(null);
        } else {
          field.onChange((values?.floatValue ?? 0) * 100);
        }
      }}
      allowLeadingZeros={false}
      allowNegative={false}
      decimalScale={2}
      fixedDecimalScale
      thousandsGroupStyle="thousand"
      thousandSeparator=","
      {...textFieldProps}
    />
  );
}
